import React, { useState, useEffect } from 'react';
import { User, Mail, Shield, Calendar, Briefcase, Building2, Globe, Bell, Lock, Check, AlertCircle, Eye, EyeOff } from 'lucide-react';
import api from '../api';
import { Breadcrumbs } from './Breadcrumbs';

const TIMEZONE_OPTIONS = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Sao_Paulo',
  'Europe/London',
  'Europe/Berlin',
  'Europe/Paris',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Shanghai',
  'Asia/Tokyo',
  'Australia/Sydney',
  'Pacific/Auckland',
];

function UserProfile({ user, onUserUpdate }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  // Edit state
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [department, setDepartment] = useState('');
  const [timezone, setTimezone] = useState('');

  // Password state
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const data = await api.getMe();
      setProfile(data);
      setUsername(data.username || '');
      setDisplayName(data.display_name || '');
      setJobTitle(data.job_title || '');
      setDepartment(data.department || '');
      setTimezone(data.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || '');
    } catch (err) {
      console.error('Failed to load profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      const updatedProfile = await api.updateProfile({
        username: username || undefined,
        display_name: displayName || undefined,
        job_title: jobTitle || undefined,
        department: department || undefined,
        timezone: timezone || undefined,
      });
      setProfile(updatedProfile);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      if (onUserUpdate) onUserUpdate(updatedProfile);
    } catch (err) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    if (currentPassword === newPassword) {
      setPasswordError('New password must be different from current password');
      return;
    }

    setChangingPassword(true);
    try {
      await api.changeOwnPassword(currentPassword, newPassword);
      setPasswordSuccess('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowPasswordSection(false);
      setTimeout(() => setPasswordSuccess(''), 4000);
    } catch (err) {
      setPasswordError(err.message || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const breadcrumbs = [
    { label: 'Dashboard', path: '/' },
    { label: 'Profile', path: '/profile' },
  ];

  if (loading) {
    return (
      <div className="min-h-full">
        <div className="px-6 lg:px-8 pt-6 pb-4">
          <Breadcrumbs items={breadcrumbs} />
        </div>
        <div className="px-6 lg:px-8 pt-2 pb-8">
          <div className="max-w-2xl space-y-6">
            {[1, 2, 3].map(i => <div key={i} className="h-24 skeleton rounded-xl" />)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full" data-testid="user-profile-page">
      {/* Breadcrumbs */}
      <div className="px-6 lg:px-8 pt-6 pb-4">
        <Breadcrumbs items={breadcrumbs} />
      </div>

      {/* Page header */}
      <div className="px-6 lg:px-8 pt-2 pb-6">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-primary/15 flex items-center justify-center text-primary text-2xl font-bold">
            {(displayName || username || profile?.email || 'U').charAt(0).toUpperCase()}
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight" data-testid="profile-title">
              {displayName || username || 'Your Profile'}
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5 flex items-center gap-2">
              <Mail className="h-3.5 w-3.5" /> {profile?.email}
              <span className="mx-1 text-border">|</span>
              <Shield className="h-3.5 w-3.5" />
              <span className="capitalize px-1.5 py-0.5 bg-primary/10 text-primary text-xs font-medium rounded-md">{profile?.role}</span>
            </p>
          </div>
        </div>
      </div>

      <div className="px-6 lg:px-8 pb-8">
        <div className="max-w-2xl space-y-8">

          {/* Profile form */}
          <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="profile-form-card">
            <div className="p-5 border-b border-border">
              <h2 className="text-base font-semibold flex items-center gap-2">
                <User className="h-4 w-4 text-primary" /> Personal Information
              </h2>
              <p className="text-sm text-muted-foreground mt-0.5">Update your profile details visible to your team</p>
            </div>
            <form onSubmit={handleSaveProfile} className="p-5 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium mb-2">Username</label>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="w-full px-4 py-2.5 border border-input rounded-xl bg-background focus:outline-none focus:ring-2 focus:ring-ring text-sm"
                    placeholder="johndoe"
                    data-testid="profile-username-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Display Name</label>
                  <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    className="w-full px-4 py-2.5 border border-input rounded-xl bg-background focus:outline-none focus:ring-2 focus:ring-ring text-sm"
                    placeholder="John Doe"
                    data-testid="profile-displayname-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 flex items-center gap-1.5"><Briefcase className="h-3.5 w-3.5 text-muted-foreground" /> Job Title</label>
                  <input
                    type="text"
                    value={jobTitle}
                    onChange={(e) => setJobTitle(e.target.value)}
                    className="w-full px-4 py-2.5 border border-input rounded-xl bg-background focus:outline-none focus:ring-2 focus:ring-ring text-sm"
                    placeholder="Product Manager"
                    data-testid="profile-jobtitle-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5 text-muted-foreground" /> Department</label>
                  <input
                    type="text"
                    value={department}
                    onChange={(e) => setDepartment(e.target.value)}
                    className="w-full px-4 py-2.5 border border-input rounded-xl bg-background focus:outline-none focus:ring-2 focus:ring-ring text-sm"
                    placeholder="Product"
                    data-testid="profile-department-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 flex items-center gap-1.5"><Globe className="h-3.5 w-3.5 text-muted-foreground" /> Timezone</label>
                <select
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                  className="w-full px-4 py-2.5 border border-input rounded-xl bg-background focus:outline-none focus:ring-2 focus:ring-ring text-sm"
                  data-testid="profile-timezone-select"
                >
                  <option value="">Select timezone</option>
                  {TIMEZONE_OPTIONS.map(tz => (
                    <option key={tz} value={tz}>{tz.replace(/_/g, ' ')}</option>
                  ))}
                </select>
              </div>

              {/* Read-only info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-3 border-t border-border">
                <div>
                  <label className="block text-xs text-muted-foreground mb-1 flex items-center gap-1"><Mail className="h-3 w-3" /> Email</label>
                  <p className="text-sm font-medium text-foreground/80">{profile?.email}</p>
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground mb-1 flex items-center gap-1"><Calendar className="h-3 w-3" /> Member Since</label>
                  <p className="text-sm font-medium text-foreground/80">{profile?.created_at ? new Date(profile.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A'}</p>
                </div>
              </div>

              {error && (
                <div className="flex items-center gap-2 px-4 py-3 bg-destructive/10 border border-destructive/20 rounded-lg text-sm text-destructive" data-testid="profile-error">
                  <AlertCircle className="h-4 w-4 shrink-0" /> {error}
                </div>
              )}

              <div className="flex items-center gap-3 pt-2">
                <button
                  type="submit"
                  disabled={saving}
                  className="px-6 py-2.5 bg-primary text-primary-foreground rounded-xl hover:opacity-90 font-semibold text-sm shadow-sm shadow-primary/20 btn-press transition-opacity disabled:opacity-50 flex items-center gap-2"
                  data-testid="save-profile-button"
                >
                  {saving ? (
                    <><span className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full" /> Saving...</>
                  ) : saved ? (
                    <><Check className="h-4 w-4" /> Saved</>
                  ) : (
                    'Save Changes'
                  )}
                </button>
                {saved && <span className="text-sm text-primary font-medium">Profile updated</span>}
              </div>
            </form>
          </div>

          {/* Password section */}
          <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="password-section-card">
            <div className="p-5 border-b border-border flex items-center justify-between">
              <div>
                <h2 className="text-base font-semibold flex items-center gap-2">
                  <Lock className="h-4 w-4 text-primary" /> Security
                </h2>
                <p className="text-sm text-muted-foreground mt-0.5">Manage your password</p>
              </div>
              {!showPasswordSection && (
                <button
                  onClick={() => setShowPasswordSection(true)}
                  className="px-4 py-2 bg-secondary text-secondary-foreground rounded-xl hover:bg-secondary/80 text-sm font-medium transition-colors"
                  data-testid="change-password-toggle"
                >
                  Change Password
                </button>
              )}
            </div>

            {passwordSuccess && (
              <div className="mx-5 mt-5 flex items-center gap-2 px-4 py-3 bg-primary/10 border border-primary/20 rounded-lg text-sm text-primary" data-testid="password-success">
                <Check className="h-4 w-4 shrink-0" /> {passwordSuccess}
              </div>
            )}

            {showPasswordSection && (
              <form onSubmit={handleChangePassword} className="p-5 space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Current Password</label>
                  <div className="relative">
                    <input
                      type={showCurrentPw ? 'text' : 'password'}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      required
                      className="w-full px-4 py-2.5 pr-10 border border-input rounded-xl bg-background focus:outline-none focus:ring-2 focus:ring-ring text-sm"
                      placeholder="Enter current password"
                      data-testid="current-password-input"
                    />
                    <button type="button" onClick={() => setShowCurrentPw(!showCurrentPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                      {showCurrentPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">New Password</label>
                  <div className="relative">
                    <input
                      type={showNewPw ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      minLength={6}
                      className="w-full px-4 py-2.5 pr-10 border border-input rounded-xl bg-background focus:outline-none focus:ring-2 focus:ring-ring text-sm"
                      placeholder="Minimum 6 characters"
                      data-testid="new-password-input"
                    />
                    <button type="button" onClick={() => setShowNewPw(!showNewPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                      {showNewPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {newPassword && newPassword.length < 6 && (
                    <p className="text-xs text-destructive mt-1">Must be at least 6 characters</p>
                  )}
                  {newPassword && newPassword.length >= 6 && (
                    <p className="text-xs text-primary mt-1 flex items-center gap-1"><Check className="h-3 w-3" /> Strong enough</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Confirm New Password</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="w-full px-4 py-2.5 border border-input rounded-xl bg-background focus:outline-none focus:ring-2 focus:ring-ring text-sm"
                    placeholder="Re-enter new password"
                    data-testid="confirm-password-input"
                  />
                  {confirmPassword && confirmPassword !== newPassword && (
                    <p className="text-xs text-destructive mt-1">Passwords do not match</p>
                  )}
                  {confirmPassword && confirmPassword === newPassword && confirmPassword.length >= 6 && (
                    <p className="text-xs text-primary mt-1 flex items-center gap-1"><Check className="h-3 w-3" /> Passwords match</p>
                  )}
                </div>

                {passwordError && (
                  <div className="flex items-center gap-2 px-4 py-3 bg-destructive/10 border border-destructive/20 rounded-lg text-sm text-destructive" data-testid="password-error">
                    <AlertCircle className="h-4 w-4 shrink-0" /> {passwordError}
                  </div>
                )}

                <div className="flex gap-3 pt-2">
                  <button
                    type="submit"
                    disabled={changingPassword}
                    className="px-6 py-2.5 bg-primary text-primary-foreground rounded-xl hover:opacity-90 font-semibold text-sm shadow-sm shadow-primary/20 btn-press transition-opacity disabled:opacity-50 flex items-center gap-2"
                    data-testid="submit-password-button"
                  >
                    {changingPassword ? (
                      <><span className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full" /> Changing...</>
                    ) : (
                      'Update Password'
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowPasswordSection(false); setPasswordError(''); setCurrentPassword(''); setNewPassword(''); setConfirmPassword(''); }}
                    className="px-5 py-2.5 bg-secondary text-secondary-foreground rounded-xl hover:bg-secondary/80 text-sm font-medium transition-colors"
                    data-testid="cancel-password-button"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* Account info */}
          <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="account-info-card">
            <div className="p-5 border-b border-border">
              <h2 className="text-base font-semibold flex items-center gap-2">
                <Shield className="h-4 w-4 text-primary" /> Account
              </h2>
            </div>
            <div className="p-5">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">Role</label>
                  <p className="text-sm font-medium capitalize">{profile?.role}</p>
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">Account ID</label>
                  <p className="text-xs font-mono text-muted-foreground truncate">{profile?._id}</p>
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">Last Updated</label>
                  <p className="text-sm font-medium">{profile?.updated_at ? new Date(profile.updated_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'Never'}</p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default UserProfile;
